import React, { useEffect, useState } from "react";
import {
  listGiftCardTemplates, createGiftCardTemplate, updateGiftCardTemplate, deleteGiftCardTemplate,
  listGiftCards, setGiftCardStatus, deleteGiftCard,
} from "../../services/giftCards";
import { autoTranslatePatch } from "../../services/translation";
import {
  Plus, Trash2, X, Gift, ToggleLeft, ToggleRight, Edit3, Copy,
  CheckCircle2, Ban, RotateCcw,
} from "lucide-react";

const EMPTY_TPL = {
  name: "", description: "", amount_azn: 50, image_url: "",
  multi_use: true, active: true,
};

const statusLabel = {
  active: "AKTİV", redeemed: "İŞLƏNİB", disabled: "DAYANDIRILIB",
};
const statusClass = {
  active: "text-[var(--primary)]",
  redeemed: "text-[var(--text-muted)]",
  disabled: "text-[var(--accent)]",
};

export default function AdminGiftCards() {
  const [tab, setTab] = useState("templates"); // templates | issued
  const [templates, setTemplates] = useState([]);
  const [issued, setIssued] = useState([]);
  const [editing, setEditing] = useState(null);
  const [copied, setCopied] = useState("");

  const reload = async () => {
    setTemplates(await listGiftCardTemplates());
    setIssued(await listGiftCards());
  };
  useEffect(() => { reload(); }, []);

  const save = async () => {
    if (!editing) return;
    if (!editing.name) return alert("Ad tələb olunur.");
    if (!editing.amount_azn || Number(editing.amount_azn) <= 0) return alert("Məbləğ 0-dan böyük olmalıdır.");
    const i18n = await autoTranslatePatch({
      name: editing.name || "",
      description: editing.description || "",
    });
    if (editing.id) {
      await updateGiftCardTemplate(editing.id, {
        ...i18n,
        name: editing.name,
        description: editing.description || "",
        amount_azn: Number(editing.amount_azn) || 0,
        image_url: editing.image_url || "",
        multi_use: !!editing.multi_use,
        active: !!editing.active,
      });
    } else {
      await createGiftCardTemplate({ ...editing, ...i18n });
    }
    setEditing(null);
    reload();
  };

  const toggleActive = async (t) => {
    await updateGiftCardTemplate(t.id, { active: !t.active });
    reload();
  };

  const delTpl = async (t) => {
    if (!window.confirm(`'${t.name}' silinsin?`)) return;
    await deleteGiftCardTemplate(t.id);
    reload();
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(""), 1500);
    } catch { /* ignore */ }
  };

  const toggleStatus = async (gc) => {
    const next = gc.status === "disabled" ? "active" : "disabled";
    await setGiftCardStatus(gc.id, next);
    reload();
  };

  const reactivate = async (gc) => {
    if (!window.confirm("Bu kartı yenidən aktiv etmək istəyirsiniz? (Balansı 0-dırsa yenə işlənməyəcək)")) return;
    await setGiftCardStatus(gc.id, "active");
    reload();
  };

  const delIssued = async (gc) => {
    if (!window.confirm(`${gc.code} kodlu kartı silmək istəyirsiniz? Bu addım geri qaytarılmazdır.`)) return;
    await deleteGiftCard(gc.id);
    reload();
  };

  return (
    <div className="p-10" data-testid="admin-gift-cards">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="metadata">HƏDİYYƏ KARTLARI</div>
          <h1 className="font-display text-5xl mt-1">Hədiyyə Kartı İdarəsi</h1>
          <p className="text-[var(--text-2)] text-sm mt-2 max-w-2xl">
            Şablonlar — saytda göstərilən sabit məbləğli kartlar. Müştəri kartı aldıqda avtomatik unikal kod
            generasiya olunur və onun sifariş sənədinə əlavə edilir.
          </p>
        </div>
        {tab === "templates" && (
          <button onClick={() => setEditing({ ...EMPTY_TPL })} className="btn-primary" data-testid="add-gift-template">
            <Plus size={14} /> ŞABLON ƏLAVƏ ET
          </button>
        )}
      </div>

      <div className="flex border-b border-[var(--border)] mb-6">
        <button
          onClick={() => setTab("templates")}
          className={`px-5 py-3 metadata border-b-2 -mb-px ${tab === "templates" ? "border-[var(--primary)] text-[var(--primary)]" : "border-transparent text-[var(--text-2)] hover:text-[var(--text)]"}`}
          data-testid="tab-templates"
        >
          ŞABLONLAR ({templates.length})
        </button>
        <button
          onClick={() => setTab("issued")}
          className={`px-5 py-3 metadata border-b-2 -mb-px ${tab === "issued" ? "border-[var(--primary)] text-[var(--primary)]" : "border-transparent text-[var(--text-2)] hover:text-[var(--text)]"}`}
          data-testid="tab-issued"
        >
          BURAXILMIŞ KARTLAR ({issued.length})
        </button>
      </div>

      {tab === "templates" && (
        <div className="border border-[var(--border)]">
          <div className="grid grid-cols-12 metadata px-4 py-3 border-b border-[var(--border)] bg-[var(--surface)]">
            <div className="col-span-1">ŞƏKİL</div>
            <div className="col-span-3">AD</div>
            <div className="col-span-2">MƏBLƏĞ</div>
            <div className="col-span-2">İSTİFADƏ</div>
            <div className="col-span-2">AKTİV</div>
            <div className="col-span-2 text-right">ƏMƏL</div>
          </div>
          {templates.map((t) => (
            <div key={t.id} className="grid grid-cols-12 px-4 py-3 border-b border-[var(--border-soft)] items-center text-sm" data-testid={`gift-tpl-${t.id}`}>
              <div className="col-span-1">
                {t.image_url ? (
                  <img src={t.image_url} alt="" className="w-12 h-12 object-cover border border-[var(--border)]" />
                ) : (
                  <div className="w-12 h-12 border border-[var(--border)] flex items-center justify-center text-[var(--text-muted)]"><Gift size={16} /></div>
                )}
              </div>
              <div className="col-span-3 font-display">{t.name}</div>
              <div className="col-span-2 font-mono text-[var(--primary)]">{Number(t.amount_azn || 0).toFixed(2)} ₼</div>
              <div className="col-span-2 metadata text-[var(--text-2)]">{t.multi_use ? "ÇOXLU İSTİFADƏ" : "TƏK İSTİFADƏ"}</div>
              <div className="col-span-2">
                <button onClick={() => toggleActive(t)}>
                  {t.active ? <ToggleRight size={20} className="text-[var(--primary)]" /> : <ToggleLeft size={20} className="text-[var(--text-muted)]" />}
                </button>
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                <button onClick={() => setEditing(t)} className="p-1 hover:text-[var(--primary)]" title="Düzəliş"><Edit3 size={14} /></button>
                <button onClick={() => delTpl(t)} className="p-1 hover:text-[var(--accent)]" title="Sil"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {!templates.length && <div className="p-10 text-center text-[var(--text-muted)] metadata">HƏLƏ ŞABLON YOXDUR</div>}
        </div>
      )}

      {tab === "issued" && (
        <div className="border border-[var(--border)]">
          <div className="grid grid-cols-12 metadata px-4 py-3 border-b border-[var(--border)] bg-[var(--surface)]">
            <div className="col-span-3">KOD</div>
            <div className="col-span-2">MƏBLƏĞ / BALANS</div>
            <div className="col-span-3">ALICI / QƏBUL EDƏN</div>
            <div className="col-span-2">STATUS</div>
            <div className="col-span-2 text-right">ƏMƏL</div>
          </div>
          {issued.map((gc) => (
            <div key={gc.id} className="grid grid-cols-12 px-4 py-3 border-b border-[var(--border-soft)] items-center text-sm" data-testid={`gift-issued-${gc.id}`}>
              <div className="col-span-3 font-mono text-[var(--primary)] text-xs flex items-center gap-2">
                <span>{gc.code}</span>
                <button onClick={() => copy(gc.code)} className="text-[var(--text-muted)] hover:text-[var(--primary)]" title="Kopyala">
                  {copied === gc.code ? <CheckCircle2 size={12} className="text-[var(--primary)]" /> : <Copy size={12} />}
                </button>
              </div>
              <div className="col-span-2 font-mono">
                <div>{Number(gc.initial_amount_azn || 0).toFixed(2)} ₼</div>
                <div className="text-xs text-[var(--text-muted)]">qalıb: {Number(gc.balance_azn || 0).toFixed(2)} ₼</div>
              </div>
              <div className="col-span-3 text-xs">
                <div className="text-[var(--text-2)] truncate">{gc.purchaser_email || "—"}</div>
                {gc.recipient_email || gc.recipient_name ? (
                  <div className="text-[var(--text-muted)] truncate">→ {gc.recipient_name || gc.recipient_email}</div>
                ) : null}
              </div>
              <div className={`col-span-2 metadata ${statusClass[gc.status] || "text-[var(--text-muted)]"}`}>
                {statusLabel[gc.status] || gc.status?.toUpperCase()}
                <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{gc.multi_use ? "çoxlu" : "tək"}</div>
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                {gc.status === "disabled" ? (
                  <button onClick={() => reactivate(gc)} className="p-1 hover:text-[var(--primary)]" title="Yenidən aktivləşdir"><RotateCcw size={14} /></button>
                ) : (
                  <button onClick={() => toggleStatus(gc)} className="p-1 hover:text-[var(--accent)]" title="Dayandır"><Ban size={14} /></button>
                )}
                <button onClick={() => delIssued(gc)} className="p-1 hover:text-[var(--accent)]" title="Sil"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {!issued.length && <div className="p-10 text-center text-[var(--text-muted)] metadata">HƏLƏ BURAXILMIŞ KART YOXDUR</div>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setEditing(null)}>
          <div className="bg-[var(--bg)] border border-[var(--border)] w-full max-w-2xl p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-3xl flex items-center gap-2"><Gift size={20} /> {editing.id ? "Şablonu Redaktə Et" : "Yeni Hədiyyə Kartı Şablonu"}</h2>
              <button onClick={() => setEditing(null)}><X /></button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="metadata">AD <span className="text-[var(--text-muted)] normal-case">— məs. "Klassik Hədiyyə Kartı"</span></label>
                <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" placeholder="Klassik Hədiyyə" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="gift-tpl-name" />
              </div>
              <div>
                <label className="metadata">MƏBLƏĞ (AZN)</label>
                <input type="number" min={1} className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1 font-mono" value={editing.amount_azn} onChange={(e) => setEditing({ ...editing, amount_azn: e.target.value })} data-testid="gift-tpl-amount" />
              </div>
              <div>
                <label className="metadata">ŞƏKİL URL <span className="text-[var(--text-muted)] normal-case">— opsional</span></label>
                <input className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" placeholder="https://…" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="metadata">AÇIQLAMA</label>
                <textarea rows={3} className="w-full bg-transparent border border-[var(--border)] px-3 py-2 mt-1" placeholder="Hədiyyə kartı haqqında qısa açıqlama…" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="col-span-2 flex flex-wrap gap-6 mt-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!editing.multi_use} onChange={(e) => setEditing({ ...editing, multi_use: e.target.checked })} />
                  <span><span className="metadata">ÇOXLU İSTİFADƏ</span> <span className="text-[var(--text-muted)]">— qalan məbləğ saxlanılır</span></span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />
                  <span><span className="metadata">AKTİV</span> <span className="text-[var(--text-muted)]">— saytda göstər</span></span>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
              <button onClick={() => setEditing(null)} className="btn-outline">LƏĞV ET</button>
              <button onClick={save} className="btn-primary" data-testid="gift-tpl-save">YADDA SAXLA</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
